/**
 * Illustration & Calculation State Atoms for Jotai (Co-located with Screens)
 */
import { atom } from 'jotai';
import type { CashflowRow } from '../core/calculations';

export interface IllustrationInputState {
  illustrationId?: string;
  productId: string;
  customerName: string;
  customerAge: number;
  customerGender: 'Nam' | 'Nữ';
  customerPhone?: string;
  customerEmail?: string;
  customerDob?: string;
  sumAssured: string;
  term: string;
  freq: string;
  estPremium: string;
  selectedRiders: string[];

  /**
   * Số tiền phí bảo hiểm đóng thêm hàng năm (Top-up Premium).
   * Dùng để đầu tư/tích lũy bổ sung ngoài phí bảo hiểm cơ bản của sản phẩm UL.
   * Ví dụ: 10.000.000 đ/năm.
   */
  topUpAmount: number;

  /**
   * Năm bắt đầu đóng phí đầu tư thêm (mặc định: 1 - ngay từ năm hợp đồng đầu tiên).
   */
  topUpStartYear: number;

  /**
   * Số năm liên tục duy trì đóng phí đầu tư thêm.
   */
  topUpDuration: number;

  /**
   * Chế độ tham gia Sản phẩm bổ trợ R5 - "Bảo Thân Xanh" (Tử vong & Thương tật do tai nạn).
   * - 'complimentary': Tặng kèm miễn phí theo chương trình ưu đãi sản phẩm chính (Hạn hạn 750M đ, Phí = 0 đ).
   * - 'attached': Đính kèm dạng trả phí mở rộng (Hạn mức 950M đ hoặc tùy chỉnh, có tính phí phụ trội).
   */
  r5Mode?: 'complimentary' | 'attached';

  /**
   * Gói quyền lợi của Sản phẩm bổ trợ R1 - "Sống Khỏe Mỗi Ngày" (Chăm sóc Y tế / Phí nội trú).
   * - 'silver': Gói Bạc - Hạn mức bảo vệ 125.000.000 đ/năm.
   * - 'gold': Gói Vàng - Hạn mức bảo vệ 250.000.000 đ/năm.
   * - 'platinum': Gói Bạch Kim - Hạn mức bảo vệ 500.000.000 đ/năm.
   */
  r1Package?: 'silver' | 'gold' | 'platinum';

  r2LimitCuoi?: number;
  r2LimitSom?: number;
  r2LimitDacBiet?: number;
  r3DailyAllowance?: number;
  r4PhysicalLimit?: number;
  r5SumAssured?: number;
  r5Term?: '20_years' | 'whole_life';
  riderPremiumOverrides?: Record<string, number>;
}

export const defaultIllustrationInput: IllustrationInputState = {
  productId: 'XUM',
  customerName: '',
  customerAge: 35,
  customerGender: 'Nam',
  customerDob: '',
  customerPhone: '',
  sumAssured: '1.000.000.000',
  term: '20 năm',
  freq: 'Năm',
  estPremium: '20.000.000',
  selectedRiders: [],
  topUpAmount: 0,
  topUpStartYear: 1,
  topUpDuration: 0,
  r5Mode: 'complimentary',
  r1Package: 'silver',
};

/**
 * Atom holding current illustration inputs (form parameters)
 */
export const illustrationInputAtom = atom<IllustrationInputState>(defaultIllustrationInput);

/**
 * Atom holding computed cashflow table results
 */
export const illustrationResultAtom = atom<CashflowRow[]>([]);
